/* eslint-disable camelcase */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Switch from 'react-switch';
import { makeStyles } from '@material-ui/core/styles';
import Snackbar from '@material-ui/core/Snackbar';


import {
  isLoadingSelector, isShowSnackbar,
  snackbarType, snackbarText, securityCompanySelector,
} from '../../../../store/settings/selectors';
import { getSecurityCompany, editSecurityPage } from '../../../../store/settings/actions';
import MaynLayout from '../../../Core/MainLayout';
import PageLayout from '../../../Core/PageLayout';
import TitleBlock from '../../../Core/TitleBlock';
import Dashboard from '../../../Core/Dashboard';
import SecurityIcon from '../../../Icons/Security';
import Progress from '../../../Core/Progress';
import Tooltip from '../../../Core/Tooltip';
import Input from '../../../Core/Input/Input';
import PasswordSetting from './passwordSetting';
import styles from './security.module.scss';

const useStyles = makeStyles(() => ({
  error: {
    background: '#de4343',
    color: '#fff',
  },
  success: {
    background: '#3bc39e',
    color: '#fff',
  },
}));

const minutesToHours = (value) => {
  if (value === 0) {
    return '';
  }
  return value / 60;
};

const hoursToMinutes = (value) => {
  if (value === '') {
    return 0;
  }
  return value * 60;
}

export default function Sesurity() {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();

  const isLoadind = useSelector(isLoadingSelector);
  const isSnackbar = useSelector(isShowSnackbar);
  const typeSnackbar = useSelector(snackbarType);
  const textSnackbar = t(useSelector(snackbarText));
  const security = useSelector(securityCompanySelector);

  const [settings, setSettings] = useState({
    send_password: false,
    min_length: false,
    numbers: false,
    special_chars: false,
    uppercase: false,
    notify_admin: false,
    force_logout_on_reset: false,
    auto_logout: false,
    auto_logout_after: 0,
  });
  const [min_password_length, setMin_password_length] = useState(6);
  const [login_attempts, setLogin_attempts] = useState('');

  const [invitation, setInvitation] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getSecurityCompany(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (Object.keys(security).length > 0) {
      setInvitation(security.invitation === 1);
      setSettings({
        send_password: security.send_password === 1,
        min_length: !!((security.min_password_length && security.min_password_length > 4)),
        numbers: security.numbers === 1,
        special_chars: security.special_chars === 1,
        uppercase: security.uppercase === 1,
        notify_admin: security.notify_admin === 1,
        force_logout_on_reset: security.force_logout_on_reset === 1,
        auto_logout: security.auto_logout === 1,
        auto_logout_after: minutesToHours(Math.max(60, security.auto_logout_after || 0)),
      });
      setMin_password_length((security.min_password_length && security.min_password_length > 4)
        ? security.min_password_length
        : 6);
      setLogin_attempts((security.login_attempts && security.login_attempts > 0) ? security.login_attempts : '');
    }
  }, [security]);

  const changeSecuritySettings = useCallback((payload) => {
    const data = {
      send_password: payload.send_password,
      numbers: payload.numbers,
      special_chars: payload.special_chars,
      uppercase: payload.uppercase,
      notify_admin: payload.notify_admin,
      invitation: payload.invitation === true ? 1 : 0,
      min_password_length: payload.min_length ? payload.min_password_length : null,
      login_attempts: payload.login_attempts !== '' ? payload.login_attempts : null,
      force_logout_on_reset: payload.force_logout_on_reset,
      auto_logout: payload.auto_logout,
      auto_logout_after: hoursToMinutes(payload.auto_logout_after),
    };
    dispatch(editSecurityPage(data, id));
  }, [dispatch, id]);

  const handleChangeInvitation = () => {
    setInvitation(!invitation);
    changeSecuritySettings({
      ...settings,
      invitation: !invitation,
      min_password_length,
      login_attempts,
    });
  };

  const handleChangeSettings = (event) => {
    setSettings({ ...settings, [event.target.name]: event.target.checked });
    changeSecuritySettings({
      ...settings,
      [event.target.name]: event.target.checked,
      invitation,
      min_password_length,
      login_attempts,
    });
  };

  const handleChangeLoginAttempts = (value) => {
    setLogin_attempts(value);
    changeSecuritySettings({
      ...settings,
      invitation,
      min_password_length,
      login_attempts: value,
    });
  };

  const changeMinPassword = (event) => {
    if (event.target.value < 3) {
      setMin_password_length(3);
      changeSecuritySettings({
        ...settings,
        invitation,
        min_password_length: 3,
        login_attempts,
      });
    } else if (event.target.value > 12) {
      setMin_password_length(12);
      changeSecuritySettings({
        ...settings,
        invitation,
        min_password_length: 12,
        login_attempts,
      });
    } else {
      setMin_password_length(event.target.value);
      changeSecuritySettings({
        ...settings,
        invitation,
        min_password_length: event.target.value,
        login_attempts,
      });
    }
  };

  return (
    <MaynLayout>
      <Dashboard>
        <TitleBlock
          title={t('Security')}
        >
          <SecurityIcon />
        </TitleBlock>
        <PageLayout>
          {
            isLoadind ? <Progress />
              : (
                <div className={styles.securityPage}>
                  <div className={styles.labelBlock}>
                    <Switch
                      onChange={handleChangeInvitation}
                      offColor='#808F94'
                      onColor='#0085FF'
                      uncheckedIcon={false}
                      checkedIcon={false}
                      checked={invitation}
                      height={21}
                      width={40}
                    />
                    <div className={styles.label}>{t('Invitation link via e-mail')}</div>
                    <Tooltip title={t('Invitation link via e-mail')} />
                  </div>
                  <div className={styles.formLine} />
                  <div className={styles.labelBlock}>
                    <Switch
                      onChange={() => {
                        setSettings({ ...settings, auto_logout: !settings.auto_logout })
                        changeSecuritySettings({
                          ...settings,
                          auto_logout: !settings.auto_logout,
                          invitation,
                          min_password_length,
                          login_attempts,
                        });
                      }}
                      offColor='#808F94'
                      onColor='#0085FF'
                      uncheckedIcon={false}
                      checkedIcon={false}
                      checked={settings.auto_logout}
                      height={21}
                      width={40}
                    />
                    <div className={styles.label}>{t('Auto logout in browser after')}</div>
                    <div>
                      <Input
                        value={settings.auto_logout_after}
                        min='1'
                        width={30}
                        name='auto_logout_after'
                        onChange={(e) => {
                          setSettings({ ...settings, auto_logout_after: e.target.value })
                          changeSecuritySettings({
                            ...settings,
                            auto_logout_after: e.target.value,
                            invitation,
                            min_password_length,
                            login_attempts,
                          });
                        }}
                      />
                    </div>
                    <div className={styles.label}>{t('hours')}</div>
                    <Tooltip title={t(`After {{hours}} hours automatically logout from account in the browser if no activity detected`, {hours: settings.auto_logout_after})} />
                  </div>
                  <div className={styles.formLine} />
                  <PasswordSetting
                    settings={settings}
                    handleChangeSettings={handleChangeSettings}
                    t={t}
                    min_password_length={min_password_length}
                    changeMinPassword={changeMinPassword}
                    login_attempts={login_attempts}
                    setLogin_attempts={handleChangeLoginAttempts}
                    force_logout_on_reset={settings.force_logout_on_reset}
                  />
                </div>
              )
          }

          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            ContentProps={{
              classes: {
                root: typeSnackbar === 'error' ? classes.error : classes.success,
              },
            }}
            severity='error'
            open={isSnackbar}
            message={textSnackbar}
            key='rigth'
          />
        </PageLayout>
      </Dashboard>
    </MaynLayout>
  );
}
